/**
 * Cinematic Scroll-Bound Animation Timeline Sequencer - Staggered Entry Edition
 * Smoothed playback + responsive matchMedia handling + reduced-motion support.
 */
document.addEventListener('DOMContentLoaded', () => {
  if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
    return;
  }

  gsap.registerPlugin(ScrollTrigger);

  const sequenceWrapper = document.querySelector('[data-seq-hero-wrapper]');
  const mainTextSlide = document.querySelector('.seq-hero__slide-content');
  const scrollIndicator = document.querySelector('.seq-hero__scroller-hint');
  const galleryCards = document.querySelectorAll('.seq-hero__card');

  if (!sequenceWrapper || galleryCards.length === 0) return;

  // Respect users who've asked their OS/browser to minimize motion.
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (prefersReducedMotion) {
    gsap.set(mainTextSlide, { opacity: 0 });
    gsap.set(scrollIndicator, { opacity: 0 });
    gsap.set(galleryCards, { y: '0%', opacity: 1 });
    return;
  }

  // A couple of global settings that make scrub-driven scenes feel less
  // "steppy": smoother lag interpolation and fewer forced recalculations.
  ScrollTrigger.config({ ignoreMobileResize: true });

  let masterTimeline;
  let mediaQueryContext;

  const buildTimeline = (isMobile) => {
    // Reset any transforms left over from a previous breakpoint's timeline
    gsap.set([mainTextSlide, scrollIndicator], { clearProps: 'transform,opacity' });
    gsap.set(galleryCards, { clearProps: 'transform,opacity' });

    // The actual amount of scrollable "runway" available for the reveal is
    // the gap between the wrapper's full height and the pinned frame's
    // height (that gap is what lets the section unpin and hand off to the
    // next one). 'bottom bottom' only works when the wrapper is taller
    // than the viewport — now that the wrapper can be shorter (so the next
    // section peeks in on load), we measure the real buffer in pixels
    // instead so the scrub range stays valid at any wrapper size.
    const stickyEl = sequenceWrapper.querySelector('.seq-hero__sticky-container');
    const runway = stickyEl
      ? Math.max(sequenceWrapper.offsetHeight - stickyEl.offsetHeight, 1)
      : sequenceWrapper.offsetHeight;

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: sequenceWrapper,
        start: 'top top',
        end: `+=${runway}`,
        // A lighter, velocity-based scrub reads as noticeably smoother than a
        // flat scrub value because it lets GSAP's internal lerp catch up
        // gradually rather than snapping to the exact scroll position.
        scrub: 1,
        invalidateOnRefresh: true,
        fastScrollEnd: true,
        anticipatePin: 1
      },
      defaults: {
        // "none" (linear) is the right choice for scrub-tied tweens: since
        // progress already maps 1:1 to scroll position, an eased curve here
        // just fights the scrub's own smoothing and reads as jittery.
        ease: 'none'
      }
    });

    // Fade + gently lift the intro typography as the user starts scrolling.
    tl.to(mainTextSlide, {
      opacity: 0,
      scale: 0.96,
      y: -30,
      duration: 1.4
    }, 0);

    // Mouse scroll hint disappears quickly, ahead of the rest of the scene.
    tl.to(scrollIndicator, {
      opacity: 0,
      duration: 0.4
    }, 0);

    // Staggered reveal: cards rise into place one after another. Adding a
    // slight opacity + scale change alongside the translate makes each card
    // feel like it's easing into the frame instead of just sliding up.
    tl.fromTo(galleryCards,
      { y: '105%', opacity: 0.6, scale: 0.97 },
      {
        y: '0%',
        opacity: 1,
        scale: 1,
        duration: isMobile ? 2 : 3,
        stagger: {
          each: isMobile ? 0.25 : 0.4,
          from: 'start'
        }
      },
      0.6
    );

    return tl;
  };

  // matchMedia keeps the animation tuned per breakpoint (mobile cards are
  // shorter and closer together, so a lighter stagger/duration feels right)
  // and automatically tears down/rebuilds the timeline on resize or device
  // rotation instead of leaving a stale ScrollTrigger measurement behind.
  mediaQueryContext = gsap.matchMedia();

  mediaQueryContext.add(
    {
      isMobile: '(max-width: 767px)',
      isDesktop: '(min-width: 768px)'
    },
    (context) => {
      const { isMobile } = context.conditions;
      masterTimeline = buildTimeline(isMobile);

      return () => {
        // Cleanup returned to gsap.matchMedia runs automatically when the
        // breakpoint changes, preventing duplicate/overlapping timelines.
        if (masterTimeline) {
          masterTimeline.scrollTrigger && masterTimeline.scrollTrigger.kill();
          masterTimeline.kill();
        }
      };
    }
  );

  // Recalculate pin/measurement math after images and video metadata load,
  // since late layout shifts (image dimensions, video aspect ratio) are one
  // of the most common causes of a scroll animation feeling "off" or jumpy.
  window.addEventListener('load', () => ScrollTrigger.refresh());
});